import React, { useState, useRef, useEffect } from 'react';
import { Printer, Database, Calendar, Filter, Send, MessageSquare, Trash2, ArrowLeft, Copy, Check, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { supabase } from '../lib/supabase';

interface SalesManagementProps {
  shopId: number;
  onBack: () => void;
}

const SalesManagement: React.FC<SalesManagementProps> = ({ shopId, onBack }) => {
  const [activeTab, setActiveTab] = useState<'extract' | 'reports' | 'query'>('extract');
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant', content: string }[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false); // New: Tracking extraction state
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchHistory = async () => {
      const { data, error } = await supabase
        .from('chat_history')
        .select('role, content')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: true })
        .limit(50);
      if (!error && data) setMessages(data as any);
    };
    if (activeTab === 'query') fetchHistory();
  }, [activeTab, shopId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping]);

  const triggerN8n = async (action: string, data: any) => {
    if (action !== 'chat_query') setIsExtracting(true); // Show loader for file extractions
    
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/manage-sales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, shop_id: shopId, ...data })
      });

      if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);

      const responseText = await response.text();
      if (!responseText) return;

      const result = JSON.parse(responseText);
      
      // Resilient payload extraction: looks for .body or direct object/array
      const payload = Array.isArray(result) ? result[0] : (result.body || result);

      if (action === 'chat_query') return payload?.output;

      const signedPath = payload?.downloadUrl || payload?.signedURL || payload?.body?.signedURL;
      
      if (signedPath) {
        const SUPABASE_BASE = "https://ufrrlfcxuovxgizxuowh.supabase.co";
        const cleanPath = signedPath.startsWith('/') ? signedPath : `/${signedPath}`;
        window.open(`${SUPABASE_BASE}${cleanPath}`, '_blank');
      }
    } catch (error) {
      console.error("Workflow failed:", error);
      alert("Connection error: The data assistant is currently unreachable. Please check your network or CORS settings.");
    } finally {
      setIsExtracting(false); // Hide loader regardless of success or failure
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || isTyping) return;
    const userMsg = chatInput;
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatInput("");
    setIsTyping(true);

    const aiResponse = await triggerN8n('chat_query', { prompt: userMsg });
    setMessages(prev => [...prev, { role: 'assistant', content: aiResponse || "I couldn't process that query." }]);
    setIsTyping(false);
  };

  const copyToClipboard = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedId(index);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100 max-w-6xl mx-auto min-h-[700px]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Sales Management</h2>
          <p className="text-gray-500 text-sm">Shop ID: <span className="font-mono font-bold text-blue-600 bg-blue-50 px-2 rounded">{shopId}</span></p>
        </div>
        <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-blue-600 font-medium transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      <div className="flex gap-8 border-b border-gray-100 mb-8">
        {(['extract', 'reports', 'query'] as const).map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-4 px-2 capitalize font-semibold text-sm relative ${activeTab === tab ? 'text-blue-600' : 'text-gray-400'}`}>
            {tab}
            {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'extract' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ActionCard 
              title="Periodic Extraction" 
              desc="Download CSV sales data." 
              icon={<Calendar className="w-6 h-6" />} 
              onClick={(data: any) => {
                // 'data' already contains { period, month, year } from handleAction
                triggerN8n('extract_periodic', data); 
              }} 
              options={['Daily', 'Weekly', 'Monthly', 'Annual']}
              loading={isExtracting}
            />


            <ActionCard 
              title="Location-Based" 
              desc="Filter by Online vs. PoS." 
              icon={<Filter className="w-6 h-6" />} 
              onClick={(data: any) => triggerN8n('extract_location', data)} 
              options={['Online', 'PoS', 'All']}
              loading={isExtracting}
            />
          </div>
        )}

        {activeTab === 'query' && (
          <div className="flex flex-col h-[650px] border border-gray-200 rounded-2xl bg-gray-50 overflow-hidden relative">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`group relative max-w-[90%] px-5 py-3 rounded-2xl text-sm shadow-sm ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none prose prose-sm max-w-none'}`}>
                    {msg.role === 'assistant' && (
                      <button onClick={() => copyToClipboard(msg.content, i)} className="absolute -top-2 -right-2 p-1.5 bg-white border rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:text-blue-600">
                        {copiedId === i ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    {msg.role === 'user' ? msg.content : <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 bg-white border-t flex gap-3">
              <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()} placeholder="Ask the data assistant..." className="flex-1 bg-gray-50 border-none rounded-xl px-5 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              <button onClick={handleSendMessage} disabled={isTyping} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white p-3 rounded-xl transition-all"><Send className="w-5 h-5" /></button>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="flex flex-col items-center justify-center h-[400px] border-2 border-dashed border-gray-100 rounded-2xl text-gray-400">
             <Printer className="w-16 h-16 mb-4 opacity-10" />
             <p>Reporting module coming soon.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ActionCard = ({ title, desc, icon, onClick, options, loading }: any) => {
  const [mainSelection, setMainSelection] = useState(options && options.length > 0 ? options[0].toLowerCase() : '');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toLocaleString('default', { month: 'long' }).toLowerCase());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const years = Array.from({length: 5}, (_, i) => (new Date().getFullYear() - i).toString());

  const handleAction = () => {
    const payload: any = { period: mainSelection };
    if (mainSelection === 'monthly') {
      payload.month = selectedMonth;
      payload.year = selectedYear;
    } else if (mainSelection === 'annual') {
      payload.year = selectedYear;
    }
    onClick(payload);
  };

  return (
    <div className="border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all bg-white flex flex-col justify-between group">
      <div>
        <div className="flex items-start justify-between mb-6">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-colors">
            {icon}
          </div>
        </div>
        <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{desc}</p>

        <div className="space-y-3">
          <select 
            value={mainSelection} 
            onChange={(e) => setMainSelection(e.target.value)}
            disabled={loading}
            className="w-full p-2.5 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer disabled:opacity-50"
          >
            {options.map((opt: string) => (
              <option key={opt} value={opt.toLowerCase()}>{opt}</option>
            ))}
          </select>

          <div className="flex gap-2">
            {mainSelection === 'monthly' && (
              <select 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(e.target.value)}
                disabled={loading}
                className="flex-1 p-2.5 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer disabled:opacity-50"
              >
                {months.map(m => <option key={m} value={m.toLowerCase()}>{m}</option>)}
              </select>
            )}
            
            {(mainSelection === 'monthly' || mainSelection === 'annual') && (
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(e.target.value)}
                disabled={loading}
                className="flex-1 p-2.5 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer disabled:opacity-50"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>

      <button 
        onClick={handleAction}
        disabled={loading}
        className="mt-6 w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium shadow-sm active:scale-95 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Extracting...
          </>
        ) : (
          <>
            <Database className="w-4 h-4" />
            Extract Data
          </>
        )}
      </button>
    </div>
  );
};

export default SalesManagement;
